import { Vector2 } from 'three';
import type {
    ModelViewerPedestalLayout,
    ModelViewerPedestalMaterial,
} from '../Pedestal';

export interface StudioPedestalBodyLayout {
    boxEdgeRadius: number
    centerY: number
    cylinderBevel: number
    floorClearance: number
    height: number
}

export function getStudioPedestalGroundShadowOffset(
    layout:ModelViewerPedestalLayout,
):number {
    return Math.max(layout.radius * 0.002, 0.0001);
}

export function getStudioCycloramaReflectionOffset(radius:number):number {
    return Math.max(radius * 0.0005, 0.0001);
}

/**
 * Keeps the product-facing top fixed while separating the body from every
 * coplanar floor overlay. Product-derived layouts have a minimum height of
 * 0.001, so the 25% safety cap still clears the overlays at the smallest size.
 */
export function getStudioPedestalBodyLayout(
    layout:ModelViewerPedestalLayout,
    material:ModelViewerPedestalMaterial,
):StudioPedestalBodyLayout {
    const smallestDimension = Math.min(
        layout.width,
        layout.depth,
        layout.height,
    );
    const largestFloorOverlayOffset = Math.max(
        getStudioPedestalGroundShadowOffset(layout),
        getStudioCycloramaReflectionOffset(layout.radius),
    );
    const floorClearance = Math.min(
        layout.height * 0.25,
        Math.max(
            smallestDimension * 0.003,
            layout.radius * 0.0025,
            largestFloorOverlayOffset
                + Math.max(layout.height * 0.001, 0.00001),
        ),
    );
    const height = layout.height - floorClearance;
    const boxEdgeRadius = Math.min(
        layout.width,
        layout.depth,
        height,
    ) * {
        glass: 0.065,
        marble: 0.045,
        solid: 0.035,
    }[material];

    return {
        boxEdgeRadius,
        centerY: layout.topY - height / 2,
        cylinderBevel: {
            glass: Math.min(layout.radius * 0.04, height * 0.12),
            marble: Math.min(layout.radius * 0.025, height * 0.1),
            solid: 0,
        }[material],
        floorClearance,
        height,
    };
}

export function createBeveledCylinderProfile(
    radius:number,
    height:number,
    bevel:number,
):Vector2[] {
    const halfHeight = height / 2;
    const bevelSegments = 12;
    const profile = [new Vector2(0, -halfHeight)];

    for(let index = 0; index <= bevelSegments; index++) {
        const angle = -Math.PI / 2
            + (Math.PI / 2) * index / bevelSegments;
        profile.push(new Vector2(
            radius - bevel + Math.cos(angle) * bevel,
            -halfHeight + bevel + Math.sin(angle) * bevel,
        ));
    }
    for(let index = 0; index <= bevelSegments; index++) {
        const angle = (Math.PI / 2) * index / bevelSegments;
        profile.push(new Vector2(
            radius - bevel + Math.cos(angle) * bevel,
            halfHeight - bevel + Math.sin(angle) * bevel,
        ));
    }
    profile.push(new Vector2(0, halfHeight));

    return profile;
}
